/**
 * Nested Seznam switch: Provozovny | Tenanti (only when view=list).
 * Picture3+SOT D02 — nests under Seznam; never a peer top-level mode (Picture2 NON-AUTHORITY).
 */

import { useTranslation } from 'react-i18next';
import type { DiscoveryListKind } from '../discovery/discoverySearchParams.js';
import { CUSTOMER_MIN_TOUCH_TARGET_CLASS } from '../../shared/ui/customerTouchTarget.js';

export type BrowseSeznamListKindSwitchProps = {
  readonly listKind: DiscoveryListKind;
  readonly onListKindChange: (listKind: DiscoveryListKind) => void;
  readonly className?: string;
  /**
   * `nested` — inside Seznam cluster (Picture3); no outer chrome.
   * `standalone` — legacy bordered strip (tests / fallback).
   */
  readonly variant?: 'nested' | 'standalone';
};

const SEGMENT_BASE = [
  // D10 — truncate long CS labels at 320/375; keep ≥44 hit via min-h/min-w.
  'inline-flex min-h-11 min-w-0 flex-1 items-center justify-center rounded-md px-1 text-xs font-medium sm:px-2 sm:text-sm',
  'truncate',
  CUSTOMER_MIN_TOUCH_TARGET_CLASS,
  'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--color-focus-ring)]',
].join(' ');

function segmentClass(active: boolean, nested: boolean): string {
  if (nested) {
    // Nested under Seznam accent cluster (HUX-12) — contrast on accent fill via accent-foreground.
    return [
      SEGMENT_BASE,
      'min-h-11 h-11',
      active
        ? 'bg-[var(--color-accent-foreground)]/20 text-[var(--color-accent-foreground)] shadow-sm'
        : 'text-[var(--color-accent-foreground)]/80 hover:bg-[var(--color-accent-foreground)]/10 hover:text-[var(--color-accent-foreground)]',
    ].join(' ');
  }
  return [
    SEGMENT_BASE,
    active
      ? 'bg-[var(--color-accent)] text-[var(--color-accent-foreground)] shadow-sm'
      : 'bg-[var(--color-surface-elevated)] text-[var(--color-on-surface-muted)] hover:bg-[var(--color-surface-hover)] hover:text-[var(--color-on-surface)]',
  ].join(' ');
}

export function BrowseSeznamListKindSwitch({
  listKind,
  onListKindChange,
  className,
  variant = 'standalone',
}: BrowseSeznamListKindSwitchProps): JSX.Element {
  const { t } = useTranslation('customer');
  const nested = variant === 'nested';

  return (
    <div
      className={[
        nested
          ? 'flex min-w-0 flex-1 gap-0.5'
          : 'flex min-w-0 w-full max-w-sm gap-1 rounded-lg border border-[var(--color-border)] bg-[var(--color-surface)] p-1',
        className,
      ]
        .filter(Boolean)
        .join(' ')}
      role="group"
      aria-label={t('browse.discovery.listKindAria', {
        defaultValue: 'Typ seznamu',
      })}
      data-testid="customer-browse-listkind-switch"
      data-variant={variant}
    >
      <button
        type="button"
        className={segmentClass(listKind === 'salesPoint', nested)}
        aria-pressed={listKind === 'salesPoint'}
        data-testid="customer-browse-listkind-salesPoint"
        onClick={() => {
          onListKindChange('salesPoint');
        }}
      >
        {t('browse.discovery.listKindSalesPoint', { defaultValue: 'Provozovny' })}
      </button>
      <button
        type="button"
        className={segmentClass(listKind === 'tenant', nested)}
        aria-pressed={listKind === 'tenant'}
        data-testid="customer-browse-listkind-tenant"
        onClick={() => {
          onListKindChange('tenant');
        }}
      >
        {t('browse.discovery.listKindTenant', {
          defaultValue: 'Business partners',
        })}
      </button>
    </div>
  );
}
